import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Carries the node that instances a GLB to the `GLBSceneRoot` of that GLB: its inline override
 * children and the visibility parent it passes on. The instancing node and the synthesised
 * `GLBSceneRoot` are two components, so the root cannot see that node. In Godot that node is the
 * GLB root.
 */
import { createContext, useContext, useMemo } from 'react';
import { parseNode3D } from '../../../nodes/base/node3d/parser';
import { useUniqueNamePaths } from '../../useUniqueNames';
import { liftOverrideChildren } from './glbNodeOverrides';
import { visibilityParentOf } from '../../../godot/scene/visibilityParent';
import { useVisibilityParent } from '../../visibilityRange/VisibilityParentContext';
import { useInstancedScenePaths } from '../../contexts/InstancedScenePathsContext';
/** Null for a GLB opened as the scene itself, which no node instances. */
const GlbInstanceContext = createContext(null);
GlbInstanceContext.displayName = 'GlbInstanceContext';
const SCENE_ROOT = {
    rawProperties: Object.freeze({}),
    overrides: [],
    visibilityParent: null,
    hostScenePaths: [],
};
/** The node that instances this GLB, or, for a GLB opened as the scene, its own root at `nodePath`. */
export function useGlbRoot(nodePath) {
    const instance = useContext(GlbInstanceContext);
    return useMemo(() => ({ ...(instance ?? SCENE_ROOT), path: nodePath }), [instance, nodePath]);
}
export function GlbInstanceProvider({ node, path, glb, children }) {
    // The GLB root is a Node3D in Godot, so it passes on a visibility parent.
    const inherited = useVisibilityParent();
    const uniquePaths = useUniqueNamePaths(path);
    const hostScenePaths = useInstancedScenePaths();
    const own = parseNode3D({ type: 'node', attributes: { name: node.name } }, node.rawProperties).visibility_parent;
    const visibilityParent = visibilityParentOf(path, own, inherited, uniquePaths);
    const instance = useMemo(() => ({
        rawProperties: node.rawProperties,
        overrides: liftOverrideChildren(node.children),
        visibilityParent,
        hostScenePaths,
        glb,
    }), [node.rawProperties, node.children, visibilityParent, hostScenePaths, glb]);
    return _jsx(GlbInstanceContext.Provider, { value: instance, children: children });
}
//# sourceMappingURL=GlbInstanceContext.js.map